import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, AlertCircle, ShieldCheck, RefreshCw, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_API_URL}/api`;
const HERO_IMG = "https://images.unsplash.com/photo-1669052700037-db884b37b2d9?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const FEATURES_KEYS = [
  "login.feature1",
  "login.feature2",
  "login.feature3",
];

export default function Login() {
  const navigate    = useNavigate();
  const location    = useLocation();
  const { setUser } = useAuth();
  const { t }       = useTranslation();
  const from = location.state?.from?.pathname || "/dashboard";

  const [step, setStep]           = useState(1);
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [showPass, setShowPass]   = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [tempToken, setTempToken] = useState("");
  const [otp, setOtp]             = useState("");
  const [maskedEmail, setMasked]  = useState("");
  const [resending, setResending] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");

  const handleGoogleLogin = () => {
    const clientId    = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/callback`);
    const scope       = encodeURIComponent("openid email profile");
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&access_type=offline&prompt=select_account`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      if (res.data.requires_otp) {
        setTempToken(res.data.temp_token); setMasked(res.data.email); setStep(2);
        if (res.data.dev_otp) { setOtp(res.data.dev_otp); toast.success(`[DEV] OTP: ${res.data.dev_otp}`); }
        else { toast.success(t("login.otpSent")); }
      }
    } catch (err) {
      // Handle email not verified error (403)
      if (err.response?.status === 403) {
        setUnverifiedEmail(email);
        setStep(3); // Step 3 = email not verified
        setError(err.response?.data?.detail || "Email belum diverifikasi");
      } else if (err.response?.status === 429) {
        setError("Terlalu banyak permintaan. Coba lagi dalam 15 menit.");
      } else {
        setError(err.response?.data?.detail || t("login.failed"));
      }
    }
    finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { setError(t("login.otpDigits")); return; }
    setError(null); setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, { temp_token: tempToken, otp }, { withCredentials: true });
      setUser(res.data);
      toast.success(t("login.welcomeBack", { name: res.data.name }));
      navigate(from, { replace: true });
    } catch (err) { setError(err.response?.data?.detail || t("login.otpInvalid")); }
    finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true);
    try { await axios.post(`${API}/auth/resend-otp`, { temp_token: tempToken }); toast.success(t("login.otpResent")); setOtp(""); }
    catch { toast.error(t("login.otpResendFail")); }
    finally { setResending(false); }
  };

  const handleResendVerification = async () => {
    setResending(true);
    setError(null);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: unverifiedEmail });
      toast.success("Email verifikasi telah dikirim ulang");
    } catch (err) {
      if (err.response?.status === 429) {
        setError("Terlalu banyak permintaan. Coba lagi dalam 15 menit.");
      } else {
        setError(err.response?.data?.detail || "Gagal mengirim ulang email");
      }
    } finally {
      setResending(false);
    }
  };

  const maskEmail = (e) => { if (!e) return ""; const [u, d] = e.split("@"); return `${u.slice(0,2)}***@${d}`; };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* LEFT — Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-sm">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="space-y-6">
                <div>
                  <h2 className="font-heading text-2xl font-bold tracking-tight">{t("login.title")}</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("login.subtitle")}{" "}
                    <Link to="/register" className="text-brand-primary hover:underline font-medium" data-testid="go-to-register">{t("login.signupLink")}</Link>
                  </p>
                </div>
                <Button onClick={handleGoogleLogin} variant="outline" className="w-full rounded-md gap-3 font-medium" data-testid="google-login-btn">
                  <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  {t("login.google")}
                </Button>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                  <div className="relative flex justify-center text-xs"><span className="bg-background px-2 text-muted-foreground font-mono">{t("login.divider")}</span></div>
                </div>
                <form onSubmit={handleLogin} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-sm bg-red-500/10 border border-red-500/30" data-testid="login-error">
                      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" /><p className="text-sm text-red-400">{error}</p>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("login.email")}</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required className="pl-9 rounded-sm text-sm" data-testid="login-email" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("login.password")}</label>
                      <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-brand-primary transition-colors" data-testid="forgot-password-link">{t("login.forgotPassword")}</Link>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="pl-9 pr-9 rounded-sm text-sm" data-testid="login-password" />
                      <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showPass ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white font-medium" data-testid="login-submit-btn">
                    {loading ? t("common.loading") : t("login.submit")}
                  </Button>
                </form>
              </motion.div>
            )}
            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 16 }} className="space-y-6">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center mx-auto mb-4">
                    <ShieldCheck className="w-7 h-7 text-brand-primary" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold">{t("login.otpTitle")}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{t("login.otpDesc")} <span className="font-mono text-foreground">{maskEmail(maskedEmail)}</span></p>
                </div>
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  {error && (<div className="flex items-center gap-2 p-3 rounded-sm bg-red-500/10 border border-red-500/30" data-testid="otp-error"><AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" /><p className="text-sm text-red-400">{error}</p></div>)}
                  <Input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="000000" required autoFocus className="rounded-sm text-center text-2xl font-mono tracking-widest h-14" data-testid="otp-input" />
                  <Button type="submit" disabled={loading || otp.length !== 6} className="w-full rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white font-medium" data-testid="verify-otp-btn">
                    {loading ? t("login.otpVerifying") : t("login.otpSubmit")}
                  </Button>
                </form>
                <div className="text-center space-y-2">
                  <button onClick={handleResend} disabled={resending} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mx-auto" data-testid="resend-otp-btn">
                    <RefreshCw className={`w-3.5 h-3.5 ${resending ? "animate-spin" : ""}`} />{resending ? t("login.otpResending") : t("login.otpResendBtn")}
                  </button>
                  <button onClick={() => { setStep(1); setOtp(""); setError(null); }} className="text-xs text-muted-foreground hover:text-foreground block mx-auto" data-testid="back-to-login-btn">{t("login.backToLogin")}</button>
                </div>
              </motion.div>
            )}
            {step === 3 && (
              <motion.div key="s3" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 16 }} className="space-y-6 text-center">
                <div>
                  <div className="w-14 h-14 rounded-full bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-7 h-7 text-yellow-600" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold">Email Belum Diverifikasi</h2>
                  <p className="text-sm text-muted-foreground mt-2">
                    Akun Anda perlu diverifikasi terlebih dahulu
                  </p>
                </div>
                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-sm bg-red-500/10 border border-red-500/30">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left space-y-2">
                  <h3 className="text-sm font-semibold text-blue-900">Apa yang harus dilakukan:</h3>
                  <ol className="text-xs text-blue-700 space-y-1.5 list-decimal list-inside">
                    <li>Klik tombol "Kirim Email Verifikasi" di bawah</li>
                    <li>Buka email yang diterima</li>
                    <li>Klik link verifikasi dalam email</li>
                  </ol>
                </div>
                <div className="space-y-3">
                  <button
                    onClick={handleResendVerification}
                    disabled={resending}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    {resending && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                    {resending ? "Mengirim..." : "Kirim Email Verifikasi"}
                  </button>
                  <button
                    onClick={() => {
                      setStep(1);
                      setError(null);
                      setPassword("");
                    }}
                    className="text-sm text-muted-foreground hover:text-foreground font-medium transition"
                  >
                    Kembali ke login
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* RIGHT — Hero Info + Image */}
      <div className="hidden lg:flex w-[50%] relative overflow-hidden border-l border-border" data-testid="login-hero-panel">
        <img src={HERO_IMG} alt="Veriksa Hero" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/70 to-brand-primary/30" />
        <div className="relative z-10 flex flex-col justify-center p-14 text-foreground">
          <div className="mb-8">
            <span className="text-xs font-mono uppercase tracking-widest text-brand-primary">{t("login.heroTag")}</span>
            <h2 className="font-heading text-4xl font-bold tracking-tight mt-3 leading-tight">
              {t("login.heroTitle")}
            </h2>
            <p className="text-muted-foreground mt-4 text-sm leading-relaxed max-w-sm">
              {t("login.heroDesc")}
            </p>
          </div>
          <div className="space-y-3">
            {FEATURES_KEYS.map(k => (
              <div key={k} className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-brand-primary mt-0.5 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">{t(k)}</span>
              </div>
            ))}
          </div>
          <div className="mt-10 pt-8 border-t border-border/40">
            <p className="text-xs font-mono text-muted-foreground/60">© 2026 Veriksa · Powered by Gemini 2.5 Pro</p>
          </div>
        </div>
      </div>
    </div>
  );
}
